import { showNotification } from "@mantine/notifications";
import { MedplumClient } from "@medplum/core";

const initSummaryTags = {
  diseases: [] as string[],
  drugs: [] as string[],
  observations: [] as string[],
}


const aiHandler = async (medplum: MedplumClient, payload: any) => {
  try {
    const res = await medplum.executeBot(
      {
        system: 'https://api-stage.healthconnect.systems/bots',
        value: 'ai-recipe-service'
      }
      , payload);
    return res;
  } catch (err) {
    console.error("Unexpected error:", err);
  }
}


type Category = 'disease' | 'drug' | 'observation';

interface ParsedResult {
  recommendation: string;
  disease: string[];
  drug: string[];
  observation: string[];
}

function parseAndCategorize(input: string): ParsedResult {
  const parseColor = (colorString: string): number => {
    const hex = colorString.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16) / 255;
    const g = parseInt(hex.substr(2, 2), 16) / 255;
    const b = parseInt(hex.substr(4, 2), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h: number;

    if (max === min) {
      h = 0;
    } else if (max === r) {
      h = 60 * ((g - b) / (max - min));
    } else if (max === g) {
      h = 60 * (2 + (b - r) / (max - min));
    } else {
      h = 60 * (4 + (r - g) / (max - min));
    }

    h = h < 0 ? h + 360 : h;
    return h;
  };

  const categorizeByHue = (hue: number): Category => {
    if (hue >= 330 || hue < 30) return 'disease';  // Red hues
    if (hue >= 90 && hue < 150) return 'drug';     // Green hues
    return 'observation';                          // Other hues (including blue)
  };

  const regex = /<span style="color:(#[A-Fa-f0-9]{6})">(.*?)<\/span>/g;
  const matches = Array.from(input.matchAll(regex));

  const categories: Record<Category, string[]> = {
    disease: [],
    drug: [],
    observation: []
  };

  matches.forEach(match => {
    const [, color, content] = match;
    const hue = parseColor(color);
    const category = categorizeByHue(hue);
    categories[category].push(content.trim());
  });

  return {
    recommendation: input.replaceAll('**-**', '').replaceAll('**', ''),
    ...categories
  };
}

const getSummary = async (medplum: MedplumClient, patientid: string,) => {
  const summary = await aiHandler(medplum, { eventType: 'summary', patientid });

  
  try {
    const parsedRes = summary?.body
    
    if (parsedRes) {
      
      let parsedSumm = parseAndCategorize(parsedRes)
      const newparsed =addInputToJson(parsedSumm,summary.input)
      
      return newparsed;
    }
    else throw new Error("Invalid Summary - Please try again")
  } catch (err) {
    console.error("Unexpected error:", err);
  }
}

const getCardioPrediction = async (medplum: MedplumClient, patientid: string,) => {
  const summary = await aiHandler(medplum, { eventType: 'prediction-cardio', patientid });
  try {
    const parsedSumm = JSON.parse(summary.body)
          const newparsed =addInputToJson(parsedSumm,summary?.input)

    return newparsed
  } catch (err) {
    console.error("Unexpected error:", err);
    return null
  }
}
export const getSymptoms =async (medplum:MedplumClient,patientid:string,symptoms:any)=>{
   const symptom = await aiHandler(medplum, { eventType: 'prediction-disease', patientid ,symptoms});
  try {
    const parsedSymtom = symptom.body
              const newparsed =addInputToJson(parsedSymtom,symptom?.input)

    return newparsed
  } catch (err) {
    console.error("Unexpected error:", err);
    return null
  }
}

const getLOSPrediction = async (medplum: MedplumClient, patientid: string,) => {
  try {
    const summary = await aiHandler(medplum, { eventType: 'prediction-los', patientid });
    if (summary?.statusCode == 200 && summary?.body) {
      
      return{
        body: summary.body,
        input: summary.input,
      };}
    else throw new Error("Invalid Summary - Please try again")
  } catch (err) {
    console.error("Unexpected error:", err);
    return null
  }
}

const getReadmissionPrediction = async (medplum: MedplumClient, patientid: string) => {
  try {
    const prediction = await aiHandler(medplum, { eventType: 'prediction-readmission', patientid });
    
    if (prediction?.statusCode == 200 && prediction?.body) {
      const parsedBody = JSON.parse(prediction.body);
      return { ...parsedBody, input: prediction.input };
    } else {
      throw new Error("Invalid Prediction - Please try again");
    }
  } catch (err) {
    console.error("Unexpected error:", err);
    return null;
  }
}


const formatAiSummary = async (medplum: MedplumClient, patientid: string, setAiResponse: any, setCardioPrediction: any, setSummaryTags: any, setCardioRed?: any) => {
  setCardioPrediction(undefined);
  setAiResponse('Generating...');
  setSummaryTags(initSummaryTags);
  const summaryDetails = await getSummary(medplum, patientid)
  
  if (!summaryDetails?.recommendation) {
    setAiResponse('Please Select an AI Recipe...');
    showNotification({ color: 'red', message: 'Something went wrong while generating AI summary', autoClose: true });
    return null;
  }
  setSummaryTags({ diseases: summaryDetails?.disease ?? [], drugs: summaryDetails?.drug ?? [], observations: summaryDetails?.observation ?? [] });
  setAiResponse(summaryDetails?.recommendation ?? '');
  return summaryDetails
}

 const formatCardioSummary = async (medplum: MedplumClient, patientid: string, setAiResponse: any, setCardioPrediction: any, setSummaryTags: any, setCardioRed?: any) => {
  setCardioPrediction(undefined);
  setAiResponse('Generating...');
  setSummaryTags(initSummaryTags);
  const prediction = await getCardioPrediction(medplum, patientid)
  if (!prediction?.Result) {
    setAiResponse('Please Select an AI Recipe...');
    showNotification({ color: 'red', message: 'Something went wrong while generating Cardio Summary', autoClose: true });
    return null
  }
  let summary = ''
  if (prediction?.Result?.includes('Presence')) {
   
    const prob = prediction?.['Presence of Cardiovascular Disease Probability'] * 100;
    summary = `As per the data available, the patient has a <span style=\"color:#FF0000\">${Math.floor(prob)}%</span> probability of having <span style=\"color:#ff9000\">cardiovascular</span> disease.`;
  }
  else {
    const prob = 100 - (prediction?.['Absence of Cardiovascular Disease Probability'] * 100);
    summary = `As per the data available, the patient has a <span style=\"color:#008000\">${Math.floor(prob)}%</span> probability of having <span style=\"color:#ff9000\">cardiovascular</span> disease.`;
  }
  setCardioPrediction(prediction?.Result);
  if (setCardioRed) setCardioRed(prediction?.Result?.includes('Presence'))
  setAiResponse(summary);
  return prediction
}

const formatLosPrediction = async (medplum: MedplumClient, patientid: string, setAiResponse: any, setCardioPrediction: any, setSummaryTags: any, setCardioRed?: any) => {
  setCardioPrediction(undefined);
  setAiResponse('Generating...');
  setSummaryTags(initSummaryTags);
  const prediction = await getLOSPrediction(medplum, patientid)
  
  if (!prediction) {
    setAiResponse('Please Select an AI Recipe...');
    showNotification({ color: 'red', message: 'Something went wrong while generating Length Of Stay', autoClose: true });
    return null
  }
  let summary = ''
  const los = prediction?.body['predicted Length of Stay(LOS) in days'] as number;
  summary = `As per the data available, the patient has a predicted stay of <span style=\"color:#1c7ed6\">${los.toFixed(1)} days</span> .`;

  setCardioPrediction(`Prediction - ${los.toFixed(1)} days`);
  if (setCardioRed) setCardioRed(los>5)
  setAiResponse(summary);
  return prediction
}

const formatReadmissionPrediction = async (medplum: MedplumClient, patientid: string, setAiResponse: any, setCardioPrediction: any, setSummaryTags: any, setCardioRed?: any) => {
  setCardioPrediction(undefined);
  setAiResponse('Generating...');
  setSummaryTags(initSummaryTags);
  const prediction = await getReadmissionPrediction(medplum, patientid)
  if (!prediction) {
    setAiResponse('Please Select an AI Recipe...');
    showNotification({ color: 'red', message: 'Something went wrong while generating Length Of Stay', autoClose: true });
    return null
  }

  let summary = ''
  const prob = prediction['Readmission Time probability'] * 100 as number;
  summary = `As per the data available, the patient has a <span style=\"color:#1c7ed6\">${prob.toFixed(1)}% </span> probability of getting readmitted in <span style=\"color:#1c7ed6\">${prediction['Readmission Time in days']} days</span> .`;

  setCardioPrediction(`Prediction - ${prob.toFixed(1)}%`);
  if (setCardioRed) setCardioRed(prob>0.8)
  setAiResponse(summary);
  return prediction
}
function summarizeDisease(data: any, patient?: any): string {
  let result = `As per the data available, the patient ${patient} may have the following diseases: `;
  result += "<ol>";
  data.forEach((disease: any) => {
    let hue = getHueFromPercentage(disease.Similarity);
    result += `<li> ${disease["Disease Name"]} - Probability: <span style="background-color: hsl(${hue}, 100%, 50%)">${disease.Similarity.toFixed(2)}%</span></li>`;
  });
  result += "</ol>";
  return result;
}

function getHueFromPercentage(percentage: number): number {
  // Ensure percentage is between 0 and 100
  percentage = Math.max(0, Math.min(percentage, 100));

  // Calculate hue value where 0% is red (0 degrees) and 100% is green (120 degrees)
  // Map percentage (0-100) to hue (0-120 degrees)
  const hue = (percentage / 100) * 120; // Green is 120 degrees, Red is 0 degrees

  return hue;
}

const getMedicationResource =async(medplum:MedplumClient,patientId:any)=>{
  try{
     const res = await medplum.searchResources(
      "Condition",
      `_count=20&_offset=0&_sort=-_lastUpdated&_total=accurate&patient=Patient/${patientId}`
    );
    return res

  }catch(error){
    console.error(error)

  }
}
function extractNoteTexts(data: any) {
    // Initialize an empty array to hold note texts
    const noteTexts: any[] = [];
    
    

    // Loop through each object in the array
    data.forEach((item: { note: any[]; }) => {
      
        // Check if the object has a 'note' property and it's an array
        if (item.note && Array.isArray(item.note)) {
            // Loop through each note
            item.note.forEach((note: { text: any; }) => {
                // Check if the note has a 'text' property
                if (note.text) {
                    // Append the note text to the array
                    noteTexts.push(note.text);
                }
            });
        }
    });

    // Join all note texts with a comma and return the result
    return noteTexts.join(', ');
}function addInputToJson(jsonObj:any, newInput:any) {
    // Parse the JSON string into an object if it's a string
    if (typeof jsonObj === 'string') {
        jsonObj = JSON.parse(jsonObj);
    }

    // Add the new input value to the 'input' array
    if (!Array.isArray(jsonObj.input)) {
        jsonObj.input = [];
    }
    jsonObj.input.push(newInput);

    // Convert the object back to a JSON string (if needed)
    return jsonObj// pretty-print with 2-space indentation
}
const formatDataForSubheading = (data:any) => {
    return Object.keys(data).map(key => {
        const formattedKey = key.replace(/_/g, ' '); // Replace underscores with spaces
        return `${formattedKey}: ${data[key]}`;
    });
}



export {
  formatDataForSubheading,
  aiHandler,
  summarizeDisease,
  getSummary,
  getCardioPrediction,
  getLOSPrediction,
  getReadmissionPrediction,
  formatAiSummary,
  formatCardioSummary,
  formatLosPrediction,
  formatReadmissionPrediction,
  getMedicationResource,
  extractNoteTexts,
  parseAndCategorize,
  addInputToJson
}
